import { CustomSelect, FileUpload, Tab, type SelectOption } from '@consigli/facade';
import { usePackages, useProjectId } from '@consigli/hooks';
import { DocumentPackageType } from '@consigli/types';
import { Dispatch, FC, SetStateAction, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CompareConfig, GapsCompareMode } from './types';

interface ChooseGapsPackageProps {
  config: CompareConfig;
  onChange: (config: CompareConfig) => void;
}

export const ChooseGapsPackage: FC<ChooseGapsPackageProps> = ({ config, onChange }) => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const { packages } = usePackages(projectId, DocumentPackageType.TENDER_DOCUMENTS);
  const [showMessagePanel, setShowMessagePanel] = useState(true);

  const packageOptions: SelectOption[] = useMemo(
    () => packages.map((pkg) => ({ label: pkg.name, value: pkg.id })),
    [packages],
  );

  const tabs: { id: GapsCompareMode; label: string }[] = [
    { id: GapsCompareMode.EXISTING, label: t('bid-review.compare.tab-existing') },
    { id: GapsCompareMode.UPLOAD_NEW, label: t('bid-review.compare.tab-upload-new') },
  ];

  const setNewReferenceFiles: Dispatch<SetStateAction<File[]>> = (value) => {
    const nextFiles = typeof value === 'function' ? value(config.newReferenceFiles) : value;
    onChange({ ...config, newReferenceFiles: nextFiles });
  };

  return (
    <div>
      <p className="font-semibold text-day-neutral-dark">
        {t('bid-review.compare.choose-package-title')}
      </p>
      <p className="text-sm text-day-neutral-subtle">
        {t('bid-review.compare.choose-package-description')}
      </p>

      <div className="flex gap-6 border-b border-day-light-2">
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            label={tab.label}
            selected={config.gapsCompareMode === tab.id}
            onClick={() => onChange({ ...config, gapsCompareMode: tab.id })}
          />
        ))}
      </div>

      {config.gapsCompareMode === GapsCompareMode.EXISTING ? (
        <div className="mt-4">
          <label className="mb-1 block text-sm font-medium text-day-neutral-dark">
            {t('bid-review.compare.existing-package-label')}
          </label>
          <CustomSelect
            options={packageOptions}
            placeholder={t('package-creation.select-package')}
            noOptionsMessage={t('custom-select.no-options')}
            isClearable
            value={packageOptions.filter(
              (option) => option.value === config.existingReferencePackageId,
            )}
            onChange={(selectedOptions: SelectOption[]) =>
              onChange({
                ...config,
                existingReferencePackageId:
                  selectedOptions.length > 0 ? String(selectedOptions[0].value) : '',
              })
            }
          />
        </div>
      ) : (
        <div className="mt-4">
          <FileUpload
            files={config.newReferenceFiles}
            setFiles={setNewReferenceFiles}
            dropAreaLabel={t('document-upload.drag-n-drop-here')}
            dropAreaClassName="mt-1 h-40 w-full"
            fileButtonLabel={t('document-upload.browse-files')}
            folderButtonLabel={t('document-upload.browse-folders')}
            uploadListLabel={t('document-upload.files')}
            zeroByteFilesMessage={t('document-upload.file-zero-bytes')}
            showMessagePanel={showMessagePanel}
            setShowMessagePanel={setShowMessagePanel}
          />
        </div>
      )}
    </div>
  );
};
